import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router';
import californiaImg from '../media/icons/jtree_selfie.png';
import newyorkImg from '../media/icons/parking_lot.png';
import fishing from '../media/icons/fishing.png';
import camera from '../media/icons/camera.jpeg';
import chess from '../media/about/chess/chessbanner.jpeg';

const personalStories = [
  {
    id: 1,
    title: "California",
    backgroundImage: californiaImg,
    gradient: "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
  },
  {
    id: 2,
    title: "New York",
    backgroundImage: newyorkImg,
    gradient: "linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)",
  },
  {
    id: 3,
    title: "Abroad 2025",
    backgroundImage: camera,
    gradient: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
  },
  {
    id: 4,
    title: "Fishing",
    backgroundImage: fishing,
    gradient: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
  },
  {
    id: 5,
    title: "Chess",
    backgroundImage: chess,
    gradient: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
  }
];

const AboutSection = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(true);
  const [isAnimating, setIsAnimating] = useState(true);
  const location = useLocation();

  const currentStory = personalStories[currentIndex];

  useEffect(() => {
    if (!isPlaying) return;

    const interval = setInterval(() => {
      setIsAnimating(false);
      setTimeout(() => {
        setCurrentIndex((prevIndex) => (prevIndex + 1) % personalStories.length);
        setIsAnimating(true);
      }, 300);
    }, 10000);

    return () => clearInterval(interval);
  }, [isPlaying]);

  const handleDotClick = (index) => {
    if (index === currentIndex) return;
    setIsAnimating(false);
    setTimeout(() => {
      setCurrentIndex(index);
      setIsAnimating(true);
    }, 300);
  };

  const handlePrevious = () => {
    const newIndex = currentIndex === 0 ? personalStories.length - 1 : currentIndex - 1;
    handleDotClick(newIndex);
  };

  const handleNext = () => {
    const newIndex = currentIndex === personalStories.length - 1 ? 0 : currentIndex + 1;
    handleDotClick(newIndex);
  };

  return (
    <section id="about" className="py-8 bg-black">
      <div
        className="relative h-[85vh] w-full overflow-hidden"
        onMouseEnter={() => setIsPlaying(false)}
        onMouseLeave={() => setIsPlaying(true)}
      >
        <div
          className={`absolute inset-0 w-full h-full transition-opacity duration-500 ${isAnimating ? 'opacity-100' : 'opacity-0'
            }`}
          style={{
            background: currentStory.backgroundImage ? `url(${currentStory.backgroundImage}) center/cover` : currentStory.gradient,
          }}
        />

        <div className="absolute inset-0 bg-gradient-to-r from-black/50 via-black/30 to-transparent" />

        <div className="absolute top-8 left-8 md:left-12 lg:left-16 z-20">
          <h2 className="text-3xl md:text-4xl font-bold text-white">
            Beyond The Code
          </h2>
        </div>

        <div className="absolute bottom-0 left-0 p-8 md:p-12 w-full md:w-2/3 pb-16">
          <div className={`space-y-4 transition-all duration-500 ${isAnimating ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-5'
            }`}>
            <h3 className="text-5xl md:text-6xl lg:text-7xl font-bold leading-none tracking-tight text-white">
              {currentStory.title}
            </h3>

            <div>
              <Link
                to={`/about/${currentStory.title.toLowerCase().replace(/\s+/g, '-')}`}
                state={{ background: location }}
                className="inline-flex items-center gap-2 bg-white text-black px-6 py-3 rounded-md font-bold hover:bg-gray-200 transition-colors mt-4"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M10 12a2 2 0 100-4 2 2 0 000 4z" />
                  <path fillRule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd" />
                </svg>
                Read Story
              </Link>
            </div>
          </div>
        </div>

        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 flex items-center gap-2 z-10">
          {personalStories.map((story, index) => (
            <button
              key={story.id}
              onClick={() => handleDotClick(index)}
              className={`transition-all duration-300 ${index === currentIndex
                ? 'w-12 h-2 bg-white'
                : 'w-2 h-2 bg-white/50 hover:bg-white/70'
                } rounded-full`}
              aria-label={`Go to story ${index + 1}`}
            />
          ))}
        </div>

        {/* Left Arrow */}
        <button
          onClick={handlePrevious}
          className="absolute left-4 top-1/2 -translate-y-1/2 z-10 group"
          aria-label="Previous story"
        >
          <div className="flex items-center justify-center w-10 h-10 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 transition-all duration-300 group-hover:bg-white/20 group-hover:scale-110">
            <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </div>
        </button>

        {/* Right Arrow */}
        <button
          onClick={handleNext}
          className="absolute right-4 top-1/2 -translate-y-1/2 z-10 group"
          aria-label="Next story"
        >
          <div className="flex items-center justify-center w-10 h-10 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 transition-all duration-300 group-hover:bg-white/20 group-hover:scale-110">
            <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </div>
        </button>
      </div>
    </section>
  );
};

export default AboutSection;
